/** The guide — B of Ale's 2026-08-21 "A+B" pick; A retired by audit cut 2
 * (Ale 2026-09-05):
 *
 * B — `HelpDot`: a circled-? in every card's title row; click toggles an
 *     anchored popover with the card type's sentence. Click-outside or Esc
 *     closes. Renders only while explain mode is ON — the rail's Explain
 *     switch (audit cut 3, Ale 2026-09-05: dots stay on by default per the
 *     20 Aug ruling; the switch is that ruling's permanent toggle). The
 *     state is server-side (/api/guidance), one read per session.
 *
 * A — the first-encounter coach (a fixed scrim that dimmed the surface and
 *     one bubble over the card, "Got it" once per card type) is GONE. Its
 *     sentence was the same text the dot carries, so nothing is lost: the
 *     card is never dimmed, and the ? holds the same words.
 *
 * Keys into GUIDANCE_CONTENT — a key with no entry renders nothing.
 */

import { useEffect, useRef, useState } from 'react'
import { GUIDANCE_CONTENT } from './content'
import { useGuidanceState } from './api'

/* ── B: the ?-on-demand popover ────────────────────────────────────── */

export function HelpDot({ k }: { k: string }) {
  const state = useGuidanceState()
  const [open, setOpen] = useState(false)
  const [flip, setFlip] = useState(false)
  const wrapRef = useRef<HTMLSpanElement>(null)
  // The popover is right-anchored (suits the deck cards' right-side dots);
  // a dot early in a wide row pushes it past the LEFT edge of whatever
  // ancestor clips overflow, and it gets cut (seen in-situ on the Policy
  // rows, 2026-08-25 — the clipper is a row container, not the viewport).
  // Walk the ancestors for the tightest clipping left edge and flip to
  // left-anchoring when the popover would cross it.
  useEffect(() => {
    if (!open || !wrapRef.current) return
    const wrap = wrapRef.current
    const pop = wrap.querySelector('.helppop') as HTMLElement | null
    if (!pop) return
    let clipLeft = 0
    let anc = wrap.parentElement
    while (anc && anc !== document.body) {
      const st = getComputedStyle(anc)
      if (st.overflowX !== 'visible' || st.overflow !== 'visible') {
        clipLeft = Math.max(clipLeft, anc.getBoundingClientRect().left)
      }
      anc = anc.parentElement
    }
    if (pop.getBoundingClientRect().left < clipLeft + 8) setFlip(true)
  }, [open])
  useEffect(() => {
    if (!open) return
    // Close on CLICK, not mousedown: a mousedown-close removes the popover
    // before the click dispatches, so the same click FALLS THROUGH to
    // whatever the popover was covering — on the workbench that navigated
    // into a surface stream when the tester aimed at the next line's dot
    // (caught live 2026-08-29). With click-close, a click on covered
    // ground hits the popover, dismisses it, and does nothing else.
    const onClickAway = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false)
    }
    // Innermost first (keyboard canon, 2026-09-04): the Esc that closes
    // the popover is spent here — the drawer or page behind it stays.
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.stopPropagation()
      setOpen(false)
    }
    document.addEventListener('click', onClickAway)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('click', onClickAway)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])
  const text = GUIDANCE_CONTENT[k]
  if (!text || !state?.explain_mode) return null
  return (
    <span className="helpwrap" ref={wrapRef}>
      <button
        type="button"
        className="helpdot"
        aria-label="What is this card?"
        aria-expanded={open}
        data-testid={`helpdot-${k}`}
        onClick={(e) => {
          e.stopPropagation()
          setOpen((o) => !o)
        }}
      >
        ?
      </button>
      {open ? (
        <div
          className={`helppop${flip ? ' flip' : ''}`}
          data-testid={`helppop-${k}`}
          onClick={(e) => e.stopPropagation()}
        >
          {text}
        </div>
      ) : null}
    </span>
  )
}
